import { Button, Chip, Stack, Typography } from '@mui/material';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextView } from '../richText';
import type { DetailsValues } from '../state';
import { descriptionText, formatFileSize, isAboutEmpty } from '../state/detailsView';

import { DetailsCard } from './DetailsCard';
import type { CardEditProps } from './DetailsCard';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * What it's about (brief §3): the description, clamped to four whole lines
 * with Show more, then the attached files and links as chips. With nothing at
 * all, the empty state asks for a description or the agreement.
 */
export type AboutCardProps = {
  values: Pick<DetailsValues, 'description' | 'files' | 'links'>;
  /** Edit mode, for a card that has its form. */
  edit?: CardEditProps;
  /** The empty state's Attach file: edit, straight to the file picker. */
  onAttach?: () => void;
};

export const AboutCard = ({ values, edit, onAttach }: AboutCardProps) => {
  const { t } = useTranslation('pmDetails');
  const empty = isAboutEmpty(values);
  return (
    <DetailsCard
      icon="description"
      title={t('about.Title')}
      automationId="details-card-about"
      {...edit}
    >
      {empty ? (
        <Stack
          data-automation-id="details-about-empty"
          sx={{
            alignItems: 'flex-start',
            gap: 2,
            p: 3,
            bgcolor: 'var(--color-fog-10)',
            borderRadius: 'var(--border-radius-x-large)',
          }}
        >
          <Typography variant="bodyRegular" sx={{ color: 'var(--color-fog-80)' }}>
            {t('about.Empty')}
          </Typography>
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<MaterialSymbol name="add" size={18} />}
              onClick={(event) => {
                event.stopPropagation();
                edit?.onEdit?.();
              }}
              data-automation-id="details-about-add-description"
            >
              {t('about.AddDescription')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<MaterialSymbol name="attach_file" size={18} />}
              onClick={(event) => {
                event.stopPropagation();
                onAttach?.();
              }}
              data-automation-id="details-about-attach-file"
            >
              {t('about.AttachFile')}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack sx={{ gap: 2 }}>
          {descriptionText(values.description) !== '' ? (
            <RichTextView value={values.description} />
          ) : (
            <Typography variant="bodyRegular" sx={{ color: 'var(--color-fog-80)' }}>
              {t('about.NoDescription')}
            </Typography>
          )}
          {values.files.length + values.links.length > 0 ? (
            <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
              {/* Files are sample-only (§3): no storage, so nothing to open. */}
              {values.files.map((file) => (
                <Chip
                  key={file.id}
                  icon={<MaterialSymbol name="picture_as_pdf" size={18} />}
                  data-automation-id="details-about-file"
                  label={
                    <Stack
                      component="span"
                      direction="row"
                      sx={{ gap: 0.75, alignItems: 'baseline' }}
                    >
                      <span>{file.name}</span>
                      <Typography
                        variant="labelMono"
                        component="span"
                        sx={{ color: 'var(--color-fog-80)' }}
                      >
                        {formatFileSize(file.sizeBytes)}
                      </Typography>
                    </Stack>
                  }
                  color="fog"
                />
              ))}
              {values.links.map((link) => (
                <Chip
                  key={link.id}
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  // Opening the link must not also open the form.
                  onClick={(event: MouseEvent) => event.stopPropagation()}
                  icon={<MaterialSymbol name="link" size={18} />}
                  data-automation-id="details-about-link"
                  label={
                    <Stack component="span" direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                      <span>{link.name}</span>
                      <MaterialSymbol name="arrow_outward" size={16} />
                    </Stack>
                  }
                  color="fog"
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      )}
    </DetailsCard>
  );
};
